import {
  ActionFunctionArgs,
  Form,
  LoaderFunctionArgs,
  redirect,
  useFetcher,
  useLoaderData,
} from "react-router-dom";
import toast from "react-hot-toast";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { organizationStore } from "../../store/organization";
import { userStore } from "../../store/user";
import { GET_LIST_EVENTS_BY_ORGANIZATION } from "../../queries";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import DeleteModal from "../../components/DeleteModal";
import { usePermissions } from "../auth/api";
import { InvoiceItem } from "../invoices/type";
import { GET_INVOICE_ITEM } from "./queries";
import { UPDATE_INVOICE_ITEM } from "./mutations";
import { InvoiceItemForm, readInvoiceItemForm } from "./InvoiceItemForm";

const client = createApolloClient();
const { getState: getUserState } = userStore;
const { getState: getOrganizationState } = organizationStore;

export const invoiceItemEditLoader = async ({ params }: LoaderFunctionArgs) => {
  const { id } = params;
  const organizationId = getOrganizationState().organizationId;
  const user = getUserState().user;
  const where: any = {};
  if (organizationId) {
    where.organization_id = { _eq: organizationId };
    where.is_active = { _eq: true };
  }
  if (user?.events?.length) where.id = { _in: user.events };

  const [{ data: itemData }, { data: eventsData }] = await Promise.all([
    client.query({ query: GET_INVOICE_ITEM, variables: { id: Number(id) }, fetchPolicy: "network-only" }),
    client.query({ query: GET_LIST_EVENTS_BY_ORGANIZATION, variables: { where } }),
  ]);
  const item = itemData?.invoice_items_by_pk as InvoiceItem | null;
  if (!item) throw new Response("Invoice item not found", { status: 404 });
  return {
    item,
    events: (eventsData?.events ?? []) as { id: number; name: string }[],
  };
};

export const invoiceItemEditAction = async ({ params, request }: ActionFunctionArgs) => {
  const { id } = params;
  const body = await request.formData();
  const { organization_id: _org, ...input } = readInvoiceItemForm(body, getUserState().user?.id);
  if (!input.name) {
    toast.error("Item name is required");
    return null;
  }
  try {
    await client.mutate({ mutation: UPDATE_INVOICE_ITEM, variables: { id: Number(id), input } });
    await client.clearStore();
    toast.success("Invoice item updated");
    return redirect("/invoice-items/list");
  } catch (error) {
    toast.error("Failed to update invoice item: " + error);
    return null;
  }
};

export const InvoiceItemEdit = () => {
  const { item, events } = useLoaderData() as {
    item: InvoiceItem;
    events: { id: number; name: string }[];
  };
  const { deletePermission } = usePermissions("invoices");
  const fetcher = useFetcher();
  return (
    <Form method="post" action={`/invoice-items/${item.id}/edit`}>
      {deletePermission && (
        <div className="flex">
          <div className="ml-auto py-2">
            <DeleteModal
              onConfirm={() =>
                fetcher.submit({}, { method: "delete", action: `/invoice-items/${item.id}/delete` })
              }
            >
              {({ setOpen }) => <SecondaryButton onClick={() => setOpen(true)}>Delete</SecondaryButton>}
            </DeleteModal>
          </div>
        </div>
      )}
      <InvoiceItemForm item={item} organizationId={item.organization_id} events={events}>
        <div className="col-span-6 flex justify-end">
          <PrimaryButton type="submit">Save Invoice Item</PrimaryButton>
        </div>
      </InvoiceItemForm>
    </Form>
  );
};
